import { Button, Input } from "@ondit/ui";
import { type FormEvent, useEffect, useState } from "react";
import { AuthPanel } from "~/common/auth/panel";
import { ApiError, api, type InvitePreview, loginUrl, logout, type Me } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { Logo } from "~/shell/logo";

export function ResetPage() {
	const t = useT();
	const { locale } = useI18n();
	const token = new URLSearchParams(window.location.search).get("token") ?? "";
	const [secret, setSecret] = useState("");
	const [busy, setBusy] = useState(false);
	const [problem, setProblem] = useState<string | null>(null);

	async function submit(event: FormEvent) {
		event.preventDefault();
		setBusy(true);
		setProblem(null);
		try {
			await api.resetPassword(token, secret);
			window.location.assign("/");
		} catch (error) {
			setProblem(error instanceof ApiError ? error.localized(locale) : String(error));
			setBusy(false);
		}
	}

	return (
		<Frame title={t("reset.title")}>
			<form className="login-card" onSubmit={submit}>
				{problem && <div className="banner banner-warn">{problem}</div>}
				<div className="field">
					<label htmlFor="new-password">{t("reset.newPassword")}</label>
					<Input
						id="new-password"
						disabled={!token || busy}
						type="password"
						autoComplete="new-password"
						required
						minLength={8}
						value={secret}
						onChange={(e) => setSecret(e.target.value)}
					/>
					<span className="hint">{t("login.passwordHint")}</span>
				</div>
				<Button variant="primary" type="submit" disabled={busy || !token}>
					{busy ? t("login.working") : t("reset.save")}
				</Button>
				{!token && (
					<>
						<p className="muted">{t("reset.noToken")}</p>
						<Button asChild variant="default">
							<a href={loginUrl()}>{t("invite.signIn")}</a>
						</Button>
					</>
				)}
			</form>
		</Frame>
	);
}

function Frame({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<div className="login">
			<AuthPanel />
			<div className="login-form">
				<a className="brand login-brand" href="/">
					<Logo size="lg" />
				</a>
				<div className="login-card">
					<h1>{title}</h1>
					{children}
				</div>
			</div>
		</div>
	);
}

/**
 * Where an invitation link lands.
 *
 * Outside the shell, like the reset page and for the same reason: whoever
 * followed it may have no account here at all. The page's whole job is to say
 * which account they were invited to and which address they have to be signed
 * in as — an invitation is to an address, so a forwarded mail is a link that
 * will not work, and it has to say so rather than answering "forbidden".
 */
export function InvitePage() {
	const t = useT();
	const { locale } = useI18n();
	const token = new URLSearchParams(window.location.search).get("token") ?? "";
	const [invite, setInvite] = useState<InvitePreview | null>(null);
	const [me, setMe] = useState<Me | null>(null);
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [problem, setProblem] = useState<string | null>(null);

	useEffect(() => {
		if (!token) {
			setLoading(false);
			return;
		}
		let live = true;
		(async () => {
			// Both, and neither blocks the other: the invitation says what this
			// is, and the session says whether the person reading it is the one
			// it was sent to. `allSettled`, because a missing session is the
			// normal case here rather than a failure.
			const [preview, session] = await Promise.allSettled([api.invitePreview(token), api.me()]);
			if (!live) return;
			if (preview.status === "fulfilled") setInvite(preview.value);
			// A missing invitation gets the sentence written for it below —
			// used, withdrawn or expired, it is gone and the sender is the way
			// to a new one — rather than the server's generic "not found".
			else
				setProblem(
					preview.reason instanceof ApiError && preview.reason.status !== 404 ? preview.reason.localized(locale) : null,
				);
			setMe(session.status === "fulfilled" ? session.value : null);
			setLoading(false);
		})();
		return () => {
			live = false;
		};
	}, [token, locale]);

	/*
	 * Signing out from here rather than following the sign-out link: that
	 * link lands on the sign-in page with nowhere to go next, and the
	 * invitation the person came here with would be a mail they have to find
	 * again.
	 */
	async function switchAccount() {
		setBusy(true);
		setProblem(null);
		try {
			await logout();
			window.location.assign(loginUrl(`/invite?token=${encodeURIComponent(token)}`));
		} catch (error) {
			setProblem(error instanceof ApiError ? error.localized(locale) : String(error));
			setBusy(false);
		}
	}

	async function accept() {
		setBusy(true);
		setProblem(null);
		try {
			const { org_id } = await api.acceptInvite(token);
			// The shell opens the workspace this browser last chose, which for
			// somebody who already has one of their own is their own — and the
			// account they just joined would be a row in a menu they have no
			// reason to open.
			try {
				localStorage.setItem("ondit.org", org_id);
			} catch {
				// Storage blocked. The shell still lists the account.
			}
			// Straight into the workspace rather than a confirmation screen:
			// the thing they were invited to is the thing to show them.
			window.location.assign("/");
		} catch (error) {
			setProblem(error instanceof ApiError ? error.localized(locale) : String(error));
			setBusy(false);
		}
	}

	if (loading) return <Frame title={t("invite.title")}>{null}</Frame>;

	if (!invite) {
		return (
			<Frame title={t("invite.title")}>
				<p className="muted">{!token ? t("invite.noToken") : (problem ?? t("invite.gone"))}</p>
				<Button asChild variant="primary">
					<a href={loginUrl()}>{t("invite.signIn")}</a>
				</Button>
			</Frame>
		);
	}

	// Signed in as somebody else. Not an error yet — the way out is to sign in
	// as the address the invitation was sent to, and saying that is more use
	// than refusing after the click.
	const wrongAccount = me !== null && me.user.email.toLowerCase() !== invite.email.toLowerCase();

	return (
		<Frame title={t("invite.title")}>
			{problem && <div className="banner banner-warn">{problem}</div>}
			<p>
				{invite.invited_by
					? t("invite.fromWho", { who: invite.invited_by, org: invite.org_name })
					: t("invite.from", { org: invite.org_name })}
			</p>
			<p className="muted">
				{t("invite.as", { email: invite.email })} · {t(`members.role.${invite.role}`)}
			</p>

			{me === null ? (
				<Button asChild variant="primary">
					<a href={loginUrl(`/invite?token=${encodeURIComponent(token)}`)}>{t("invite.signIn")}</a>
				</Button>
			) : wrongAccount ? (
				<>
					<p className="muted">{t("invite.wrongAccount", { signed: me.user.email, invited: invite.email })}</p>
					<Button variant="primary" type="button" onClick={switchAccount} disabled={busy}>
						{busy ? t("login.working") : t("nav.signOut")}
					</Button>
				</>
			) : (
				<Button variant="primary" type="button" onClick={accept} disabled={busy}>
					{busy ? t("login.working") : t("invite.accept")}
				</Button>
			)}
		</Frame>
	);
}
